import { FounderAskAuthor, FounderAskReply } from '../../lib/founderAsks'

// Scale thresholds for the founder-ask context pane. All four live here so the
// pane's density behavior can be tuned in one place.
export const COMPACT_FOUNDERS = 4   // ≥ this many founders → compact founder rows (38px avatars, no pill)
export const DENSE_PARTICIPANTS = 5 // ≥ this many participants → dense rows, bare counts, footer eligible
export const COMPACT_ASK = 8        // ≥ this many total rows (founders + participants) → ask body hides, asker collapses
export const FOUNDERS_SCROLL = 7    // ≥ this many founders (i.e. >6) → founders block gets its own capped scroll

export interface ParticipantRow {
  user: FounderAskAuthor
  messageCount: number
  lastMessageAt: string
  isAsker: boolean
}

export interface FounderRow {
  user: FounderAskAuthor
  hasReplied: boolean
  firstReplyId: string | null
  firstRepliedAt: string | null
  messageCount: number
}

export interface PaneMode {
  compactFounders: boolean
  denseParticipants: boolean
  compactAsk: boolean
  foundersScroll: boolean
}

interface AskSummary {
  user_id: string
  author?: FounderAskAuthor
  created_at: string
}

// Non-founder repliers grouped by user, sorted by message count desc (ties by
// most recent message). The asker is always present, tagged, even with no
// replies — unless they're a founder, in which case the founders block owns them.
export function buildParticipantRows(ask: AskSummary, replies: FounderAskReply[]): ParticipantRow[] {
  const byUser = new Map<string, ParticipantRow>()

  for (const r of replies) {
    if (r.author_is_founder) continue
    const existing = byUser.get(r.user_id)
    if (existing) {
      existing.messageCount += 1
      if (r.created_at > existing.lastMessageAt) existing.lastMessageAt = r.created_at
    } else {
      byUser.set(r.user_id, {
        user: r.user ?? { id: r.user_id, first_name: 'Member', last_name: '', username: null, avatar_url: null },
        messageCount: 1,
        lastMessageAt: r.created_at,
        isAsker: r.user_id === ask.user_id,
      })
    }
  }

  const askerIsFounder = ask.author?.is_founder === true
  if (!askerIsFounder && !byUser.has(ask.user_id)) {
    byUser.set(ask.user_id, {
      user: ask.author ?? { id: ask.user_id, first_name: 'Member', last_name: '', username: null, avatar_url: null },
      messageCount: 0,
      lastMessageAt: ask.created_at,
      isAsker: true,
    })
  }

  return Array.from(byUser.values()).sort((a, b) => {
    if (a.messageCount !== b.messageCount) return b.messageCount - a.messageCount
    return a.lastMessageAt < b.lastMessageAt ? 1 : a.lastMessageAt > b.lastMessageAt ? -1 : 0
  })
}

// Every founder renders, replied or not. Replied founders come first in the
// order they answered; the rest sort last (the pane dims them).
export function buildFounderRows(founders: FounderAskAuthor[], replies: FounderAskReply[]): FounderRow[] {
  const rows = founders.map((founder): FounderRow => {
    const theirs = replies
      .filter(r => r.user_id === founder.id && r.author_is_founder)
      .sort((a, b) => (a.created_at < b.created_at ? -1 : a.created_at > b.created_at ? 1 : 0))
    return {
      user: founder,
      hasReplied: theirs.length > 0,
      firstReplyId: theirs[0]?.id ?? null,
      firstRepliedAt: theirs[0]?.created_at ?? null,
      messageCount: theirs.length,
    }
  })

  return rows.sort((a, b) => {
    if (a.hasReplied !== b.hasReplied) return a.hasReplied ? -1 : 1
    if (a.hasReplied && b.hasReplied) {
      return a.firstRepliedAt! < b.firstRepliedAt! ? -1 : a.firstRepliedAt! > b.firstRepliedAt! ? 1 : 0
    }
    return a.user.first_name.localeCompare(b.user.first_name)
  })
}

export function paneMode(founderCount: number, participantCount: number): PaneMode {
  return {
    compactFounders: founderCount >= COMPACT_FOUNDERS,
    denseParticipants: participantCount >= DENSE_PARTICIPANTS,
    compactAsk: founderCount + participantCount >= COMPACT_ASK,
    foundersScroll: founderCount >= FOUNDERS_SCROLL,
  }
}
